// Shared "what counts as a new arrival" rule. The list page
// (`NewArrivalsPanel`) and the Home page highlights row both call this
// so the items featured in the small Home card are drawn from exactly
// the set visible on the dedicated page — not from a wider pool that
// would surface stale items that don't even show up if the user clicks
// through.

// Always show at least this many on the list page, no matter how thin
// the recent batch is.
export const NEW_ARRIVALS_MIN_VISIBLE = 6;

// "Within a week of the newest item" — keep an entire delivery batch
// together, but a single trickle-in item a week later doesn't truncate
// the older recent items.
const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Items must arrive sorted newest-first (the backend returns them that
// way). Returns the cap-respected slice; never mutates the input.
export const selectVisibleNewArrivals = (items: IItem[]): IItem[] => {
  if (items.length <= NEW_ARRIVALS_MIN_VISIBLE) return items;
  const newest = items[0].createdAt ? new Date(items[0].createdAt).getTime() : NaN;
  if (Number.isNaN(newest)) {
    // No timestamp on the newest item — backend backfill hasn't run or
    // the server is mid-deploy. Fall back to the minimum so the page
    // (and highlights) stay useful instead of empty.
    return items.slice(0, NEW_ARRIVALS_MIN_VISIBLE);
  }
  const cutoff = newest - WINDOW_MS;
  let withinWindow = 0;
  for (const i of items) {
    const ts = i.createdAt ? new Date(i.createdAt).getTime() : NaN;
    if (Number.isNaN(ts) || ts < cutoff) break;
    withinWindow += 1;
  }
  return items.slice(0, Math.max(NEW_ARRIVALS_MIN_VISIBLE, withinWindow));
};
